import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtCurrency, fmtDate } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { useI18n } from "../../lib/i18n";
import {
    Shield, Smartphone, HeartPulse, ShieldCheck, FilePlus, AlertCircle, CheckCircle2, Clock,
} from "lucide-react";

const PRODUCT_ICONS = { loan_protection: Shield, device_protection: Smartphone, health_micro: HeartPulse };

export default function Insurance() {
    const nav = useNavigate();
    const { t } = useI18n();
    const [tab, setTab] = useState("policies");
    const [catalog, setCatalog] = useState([]);
    const [policies, setPolicies] = useState([]);
    const [claims, setClaims] = useState([]);
    const [loans, setLoans] = useState([]);
    const [dlg, setDlg] = useState(null); // null | "loan" | "device" | "health" | "claim:<policy_id>"
    const [activeClaimPolicy, setActiveClaimPolicy] = useState(null);

    const load = async () => {
        const [c, p, cl, ln] = await Promise.all([
            api.get("/insurance/catalog"),
            api.get("/insurance/policies"),
            api.get("/insurance/claims"),
            api.get("/loans").catch(() => ({ data: [] })),
        ]);
        setCatalog(c.data.products);
        setPolicies(p.data);
        setClaims(cl.data);
        setLoans(ln.data);
    };
    useEffect(() => { load(); }, []);

    return (
        <div data-testid="insurance-page">
            <MobileHeader title={t("insurance.title")} backTo="/consumer" variant="emerald">
                <p className="text-xs text-white/80 mt-3">{t("insurance.subtitle")}</p>
            </MobileHeader>

            <div className="px-5 -mt-4 pb-4 space-y-4">
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="bg-white border border-slate-200 rounded-xl p-1 grid grid-cols-3 w-full" data-testid="insurance-tabs">
                        <TabsTrigger value="policies" data-testid="tab-policies"><ShieldCheck size={14} /> {t("insurance.tab.policies")} ({policies.filter(p => p.status === "active").length})</TabsTrigger>
                        <TabsTrigger value="shop" data-testid="tab-shop"><Shield size={14} /> {t("insurance.tab.shop")}</TabsTrigger>
                        <TabsTrigger value="claims" data-testid="tab-claims"><FilePlus size={14} /> {t("insurance.tab.claims")} ({claims.length})</TabsTrigger>
                    </TabsList>
                </Tabs>

                {tab === "policies" && (
                    <>
                        {policies.length === 0 && <Empty msg={t("insurance.empty.policies")} />}
                        <div className="space-y-3">
                            {policies.map((p) => <PolicyCard key={p.id} policy={p} onClaim={() => { setActiveClaimPolicy(p); setDlg(`claim:${p.id}`); }} onReload={load} />)}
                        </div>
                    </>
                )}

                {tab === "shop" && (
                    <div className="space-y-3">
                        {catalog.map((prod) => {
                            const Icon = PRODUCT_ICONS[prod.key] || Shield;
                            return (
                                <div key={prod.key} className="bg-white rounded-2xl p-5 border border-slate-200/60 product-shadow" data-testid={`product-${prod.key}`}>
                                    <div className="flex items-start gap-3">
                                        <div className="size-12 rounded-2xl flex items-center justify-center text-white flex-shrink-0" style={{ background: prod.color }}>
                                            <Icon size={22} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display font-semibold text-[#0A1930]">{t(`insurance.product.${prod.key}`)}</div>
                                            <div className="text-xs text-slate-500">{prod.tagline}</div>
                                            <p className="text-xs text-slate-600 mt-2">{prod.description}</p>
                                            {prod.key === "health_micro" && (
                                                <div className="grid grid-cols-3 gap-1.5 mt-3">
                                                    {prod.tiers.map((tt) => (
                                                        <div key={tt.key} className="bg-slate-50 rounded-lg p-2 text-center">
                                                            <div className="text-[10px] uppercase font-bold" style={{ color: tt.color }}>{tt.name}</div>
                                                            <div className="text-xs font-bold">{fmtCurrency(tt.monthly, "NGN", { compact: true })}/mo</div>
                                                            <div className="text-[10px] text-slate-500">Cap {fmtCurrency(tt.annual_cap, "NGN", { compact: true })}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            <Button onClick={() => setDlg(prod.key.replace("_protection", "").replace("_micro", ""))} size="sm" className="mt-3 h-9 rounded-lg text-xs font-semibold" style={{ background: prod.color }} data-testid={`buy-${prod.key}`}>
                                                {t("insurance.get_cover")}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {tab === "claims" && (
                    <>
                        {claims.length === 0 && <Empty msg={t("insurance.empty.claims")} />}
                        <div className="space-y-3">
                            {claims.map((c) => <ClaimCard key={c.id} claim={c} />)}
                        </div>
                    </>
                )}
            </div>

            <LoanProtectionDialog open={dlg === "loan"} onClose={() => setDlg(null)} loans={loans} onDone={load} />
            <DeviceProtectionDialog open={dlg === "device"} onClose={() => setDlg(null)} onDone={load} />
            <HealthCoverDialog open={dlg === "health"} onClose={() => setDlg(null)} catalog={catalog} onDone={load} />
            <FileClaimDialog open={dlg && dlg.startsWith("claim:")} onClose={() => setDlg(null)} policy={activeClaimPolicy} onDone={load} />
        </div>
    );
}

function Empty({ msg }) {
    return <div className="bg-white rounded-2xl border border-slate-200/60 p-8 text-center text-sm text-slate-500 product-shadow">{msg}</div>;
}

function PolicyCard({ policy, onClaim, onReload }) {
    const { t } = useI18n();
    const Icon = PRODUCT_ICONS[policy.product] || Shield;
    const STATUS = {
        active:    { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
        cancelled: { bg: "bg-slate-100 text-slate-700 border-slate-200", icon: AlertCircle },
        expired:   { bg: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
    };
    const meta = STATUS[policy.status] || STATUS.active;
    const StatusIcon = meta.icon;

    const cancel = async () => {
        if (!confirm("Cancel this policy? Coverage stops immediately.")) return;
        try {
            await api.post(`/insurance/policies/${policy.id}/cancel`);
            toast.success("Policy cancelled");
            onReload();
        } catch (err) { toast.error("Failed"); }
    };

    return (
        <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid={`policy-${policy.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                    <div className="size-11 rounded-xl flex items-center justify-center text-white flex-shrink-0" style={{ background: policy.product === "loan_protection" ? "#0066FF" : policy.product === "device_protection" ? "#7000FF" : "#00D084" }}>
                        <Icon size={20} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="font-display font-semibold text-[#0A1930] truncate">{policy.product_name}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                            {policy.loan_product && `On ${policy.loan_product} loan`}
                            {policy.device_brand && `${policy.device_brand} ${policy.device_model}`}
                            {policy.tier_label && `${policy.tier_label} tier · cap ${fmtCurrency(policy.annual_cap_remaining)} left`}
                        </div>
                    </div>
                </div>
                <Badge className={`capitalize border ${meta.bg}`}><StatusIcon size={11} /> {policy.status}</Badge>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 pt-3 border-t border-slate-100">
                <Mini label={policy.premium_paid ? t("insurance.policy.premium") : t("insurance.policy.monthly")} value={fmtCurrency(policy.premium_paid || policy.monthly_premium || 0, "NGN", { compact: true })} />
                <Mini label={t("insurance.policy.coverage")} value={fmtCurrency(policy.coverage_amount || policy.annual_cap || 0, "NGN", { compact: true })} valueClass="text-emerald-600" />
                <Mini label={t("insurance.policy.expires")} value={fmtDate(policy.expires_at)} valueClass="text-[10px]" />
            </div>
            {policy.status === "active" && (
                <div className="mt-3 flex gap-2">
                    <Button onClick={onClaim} size="sm" className="flex-1 h-9 rounded-lg bg-[#FF6B00] hover:bg-[#cc5400] text-xs font-semibold" data-testid={`file-claim-${policy.id}`}>
                        <FilePlus size={13} /> {t("insurance.file_claim")}
                    </Button>
                    <Button onClick={cancel} size="sm" variant="outline" className="h-9 rounded-lg text-xs border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`cancel-policy-${policy.id}`}>
                        {t("insurance.cancel_policy")}
                    </Button>
                </div>
            )}
        </div>
    );
}

function ClaimCard({ claim }) {
    const { t } = useI18n();
    const STATUS = {
        review:   { bg: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock, key: "insurance.claim.under_review" },
        approved: { bg: "bg-blue-50 text-blue-700 border-blue-200", icon: CheckCircle2, key: "insurance.claim.approved" },
        paid:     { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, key: "insurance.claim.paid" },
        declined: { bg: "bg-rose-50 text-rose-700 border-rose-200", icon: AlertCircle, key: "insurance.claim.declined" },
    };
    const meta = STATUS[claim.status] || STATUS.review;
    const Icon = meta.icon;
    return (
        <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid={`claim-${claim.id}`}>
            <div className="flex items-center gap-3">
                <div className={`size-10 rounded-xl flex items-center justify-center ${meta.bg}`}><Icon size={18} /></div>
                <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-[#0A1930] truncate">{claim.reason}</div>
                    <div className="text-[11px] text-slate-500">{fmtDate(claim.created_at)} · {claim.policy_product?.replace("_", " ")}</div>
                </div>
                <div className="text-right">
                    <div className="font-mono font-bold text-sm">{fmtCurrency(claim.amount_requested)}</div>
                    <Badge className={`capitalize border ${meta.bg} mt-1 text-[10px]`}>{t(meta.key)}</Badge>
                </div>
            </div>
            {claim.decision_note && <p className="text-xs text-slate-600 mt-2 bg-slate-50 rounded-lg p-2">{claim.decision_note}</p>}
        </div>
    );
}

function Mini({ label, value, valueClass }) {
    return (
        <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
            <div className={`font-mono text-xs font-bold mt-0.5 ${valueClass || "text-[#0A1930]"}`}>{value}</div>
        </div>
    );
}

function LoanProtectionDialog({ open, onClose, loans, onDone }) {
    const { t } = useI18n();
    const [loanId, setLoanId] = useState("");
    const [busy, setBusy] = useState(false);
    const activeLoans = loans.filter((l) => !["repaid", "declined"].includes(l.status));
    const submit = async () => {
        if (!loanId) return toast.error("Pick a loan");
        setBusy(true);
        try {
            await api.post("/insurance/loan-protection", { loan_id: loanId });
            toast.success("Loan now protected");
            onDone(); onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><Shield size={18} className="text-[#0066FF]" /> {t("insurance.dlg.loan.title")}</DialogTitle>
                    <DialogDescription>{t("insurance.dlg.loan.desc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <Label className="text-xs">{t("insurance.dlg.loan.active_loans")}</Label>
                    <Select value={loanId} onValueChange={setLoanId}>
                        <SelectTrigger className="h-11 rounded-xl" data-testid="loan-select"><SelectValue placeholder={t("insurance.dlg.loan.choose")} /></SelectTrigger>
                        <SelectContent>
                            {activeLoans.length === 0 && <SelectItem value="none" disabled>No active loans</SelectItem>}
                            {activeLoans.map((l) => (
                                <SelectItem key={l.id} value={l.id}>{l.product}™ · {fmtCurrency(l.amount)} · {l.status}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {loanId && activeLoans.find(l => l.id === loanId) && (
                        <div className="bg-blue-50 rounded-xl p-3 text-sm">
                            <div className="flex justify-between"><span className="text-slate-600">{t("insurance.policy.premium")}</span><span className="font-bold">{fmtCurrency(activeLoans.find(l => l.id === loanId).amount * 0.008)}</span></div>
                            <div className="flex justify-between mt-1"><span className="text-slate-600">{t("insurance.policy.coverage")}</span><span className="font-bold text-[#0066FF]">{fmtCurrency(activeLoans.find(l => l.id === loanId).amount)}</span></div>
                        </div>
                    )}
                    <Button onClick={submit} disabled={busy || !loanId} className="w-full h-11 rounded-xl bg-[#0066FF] hover:bg-[#0052cc]" data-testid="loan-submit-btn">
                        {busy ? "…" : t("insurance.dlg.loan.submit")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function DeviceProtectionDialog({ open, onClose, onDone }) {
    const { t } = useI18n();
    const [form, setForm] = useState({ device_type: "phone", device_brand: "", device_model: "", device_value: "" });
    const [busy, setBusy] = useState(false);
    const val = parseFloat(form.device_value || 0);
    const monthly = val * 0.015;
    const coverage = val * 0.85;
    const submit = async () => {
        if (!form.device_brand || !form.device_model || val <= 0) return toast.error("Fill all fields");
        setBusy(true);
        try {
            await api.post("/insurance/device-protection", { ...form, device_value: val });
            toast.success("Device protected!");
            setForm({ device_type: "phone", device_brand: "", device_model: "", device_value: "" });
            onDone(); onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><Smartphone size={18} className="text-[#7000FF]" /> {t("insurance.dlg.device.title")}</DialogTitle>
                    <DialogDescription>{t("insurance.dlg.device.desc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                        {["phone", "laptop", "tablet"].map((dt) => (
                            <button key={dt} onClick={() => setForm((s) => ({ ...s, device_type: dt }))}
                                className={`p-3 rounded-xl border text-xs font-semibold capitalize ${form.device_type === dt ? "border-[#7000FF] bg-purple-50 text-[#7000FF]" : "border-slate-200"}`}
                                data-testid={`device-type-${dt}`}>
                                {dt}
                            </button>
                        ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs">{t("insurance.dlg.device.brand")}</Label>
                            <Input value={form.device_brand} onChange={(e) => setForm((s) => ({ ...s, device_brand: e.target.value }))} placeholder="Samsung" className="mt-1 h-11 rounded-xl" data-testid="device-brand-input" />
                        </div>
                        <div>
                            <Label className="text-xs">{t("insurance.dlg.device.model")}</Label>
                            <Input value={form.device_model} onChange={(e) => setForm((s) => ({ ...s, device_model: e.target.value }))} placeholder="Galaxy S23" className="mt-1 h-11 rounded-xl" data-testid="device-model-input" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">{t("insurance.dlg.device.value")}</Label>
                        <Input type="number" value={form.device_value} onChange={(e) => setForm((s) => ({ ...s, device_value: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="device-value-input" />
                    </div>
                    {val > 0 && (
                        <div className="bg-purple-50 rounded-xl p-3 text-sm">
                            <div className="flex justify-between"><span className="text-slate-600">{t("insurance.policy.monthly")}</span><span className="font-bold">{fmtCurrency(monthly)}</span></div>
                            <div className="flex justify-between mt-1"><span className="text-slate-600">{t("insurance.policy.coverage")}</span><span className="font-bold text-[#7000FF]">{fmtCurrency(coverage)}</span></div>
                        </div>
                    )}
                    <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-[#7000FF] hover:bg-[#5d00d6]" data-testid="device-submit-btn">
                        {busy ? "…" : t("insurance.dlg.device.submit")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function HealthCoverDialog({ open, onClose, catalog, onDone }) {
    const { t } = useI18n();
    const product = catalog.find((p) => p.key === "health_micro");
    const tiers = product?.tiers || [];
    const [tier, setTier] = useState("basic");
    const [beneficiaries, setBeneficiaries] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            await api.post("/insurance/health-cover", {
                tier,
                beneficiaries: beneficiaries.split(",").map((s) => s.trim()).filter(Boolean),
            });
            toast.success("Health cover activated");
            onDone(); onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><HeartPulse size={18} className="text-[#00D084]" /> {t("insurance.dlg.health.title")}</DialogTitle>
                    <DialogDescription>{t("insurance.dlg.health.desc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                        {tiers.map((tt) => (
                            <button key={tt.key} onClick={() => setTier(tt.key)}
                                className={`p-3 rounded-xl border text-center ${tier === tt.key ? "border-current bg-emerald-50" : "border-slate-200"}`}
                                style={{ color: tier === tt.key ? tt.color : undefined }}
                                data-testid={`health-tier-${tt.key}`}>
                                <div className="font-display font-bold">{tt.name}</div>
                                <div className="text-xs mt-1">{fmtCurrency(tt.monthly, "NGN", { compact: true })}/mo</div>
                                <div className="text-[10px] text-slate-500 mt-0.5">Cap {fmtCurrency(tt.annual_cap, "NGN", { compact: true })}</div>
                            </button>
                        ))}
                    </div>
                    <div>
                        <Label className="text-xs">{t("insurance.dlg.health.beneficiaries")}</Label>
                        <Input value={beneficiaries} onChange={(e) => setBeneficiaries(e.target.value)} placeholder="Spouse, children" className="mt-1 h-11 rounded-xl" data-testid="health-beneficiaries-input" />
                    </div>
                    <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-[#00D084] hover:bg-[#00b073]" data-testid="health-submit-btn">
                        {busy ? "…" : t("insurance.dlg.health.submit")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function FileClaimDialog({ open, onClose, policy, onDone }) {
    const { t } = useI18n();
    const [form, setForm] = useState({ reason: "", amount_requested: "" });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!form.reason || !form.amount_requested) return toast.error("Fill all fields");
        setBusy(true);
        try {
            await api.post("/insurance/claims", { policy_id: policy.id, reason: form.reason, amount_requested: parseFloat(form.amount_requested) });
            toast.success("Claim filed. We'll respond in 48 hours.");
            setForm({ reason: "", amount_requested: "" });
            onDone(); onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><FilePlus size={18} className="text-[#FF6B00]" /> {t("insurance.dlg.claim.title")}</DialogTitle>
                    <DialogDescription>{t("insurance.dlg.claim.desc")}</DialogDescription>
                </DialogHeader>
                {policy && (
                    <div className="space-y-3">
                        <div className="bg-amber-50 rounded-xl p-3 text-xs">
                            <strong>{policy.product_name}</strong> · {t("insurance.policy.coverage")}: {fmtCurrency(policy.coverage_amount || policy.annual_cap_remaining || 0)}
                        </div>
                        <div>
                            <Label className="text-xs">{t("insurance.dlg.claim.reason")}</Label>
                            <Textarea rows={3} value={form.reason} onChange={(e) => setForm((s) => ({ ...s, reason: e.target.value }))} placeholder="…" className="mt-1 rounded-xl" data-testid="claim-reason-input" />
                        </div>
                        <div>
                            <Label className="text-xs">{t("insurance.dlg.claim.amount")}</Label>
                            <Input type="number" value={form.amount_requested} onChange={(e) => setForm((s) => ({ ...s, amount_requested: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="claim-amount-input" />
                        </div>
                        <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-[#FF6B00] hover:bg-[#cc5400]" data-testid="claim-submit-btn">
                            {busy ? "…" : t("insurance.dlg.claim.submit")}
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
